import React, { useEffect, useRef } from 'react';
import { XIcon } from 'lucide-react';
import { Input } from '@rocco/components/input';
import { useUserLoggedIn } from '~/modules/user';
import type { ChatRecipient, Conversation } from '../../types';
import { getChatTitle } from '../../utils';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';
import { ConversationAvatar } from '../avatar/ConversationAvatar';

/**
 * A search input to search for contact suggestions and a list of recipients
 * (or conversation) to show when creating a new conversation.
 * @see {@link ChatBox}
 */
export function ChatBoxRecipients({
    search,
    recipients,
    conversation,
    maxRecipients,
    isOpen,
    isDisabled = false,
    onSearchChange,
    onRecipientsChange,
    onConversationRemove,
    onInputRef,
}: {
    search: string;
    recipients: ChatRecipient[];
    conversation?: Conversation;
    maxRecipients: number;
    isOpen: boolean;
    isDisabled?: boolean;
    onSearchChange: (search: string) => void;
    onRecipientsChange: (recipients: ChatRecipient[]) => void;
    onConversationRemove: (conversation: Conversation) => void;
    onInputRef?: (element: HTMLInputElement | undefined) => void;
}) {
    const recipientsContainerRef = useRef<HTMLUListElement>(null);
    const inputRef = useRef<HTMLInputElement>();
    const prevRecipientsCountRef = useRef(recipients.length);
    const { user, isLoggedIn } = useUserLoggedIn();

    // Focus the input when the chat box is opened.
    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    // Scroll to the bottom of the recipients list when a new recipient is added.
    useEffect(() => {
        const wasAdded = recipients.length > prevRecipientsCountRef.current;
        prevRecipientsCountRef.current = recipients.length;

        if (wasAdded && recipientsContainerRef.current) {
            recipientsContainerRef.current.scrollTop =
                recipientsContainerRef.current.scrollHeight;
        }
    }, [recipients.length]);

    return (
        <div className="space-y-[8px]">
            {conversation ? (
                <ul className="flex">
                    <RecipientItem
                        avatar={
                            <ConversationAvatar
                                className="size-[28px]"
                                avatarClassName="size-[28px]"
                                fallbackClassName="text-[13px]!"
                                conversation={conversation}
                                professionalID={null}
                            />
                        }
                        name={getChatTitle(
                            conversation,
                            isLoggedIn ? user.ID : 0,
                        )}
                        isDisabled={isDisabled}
                        onRemove={() => {
                            onConversationRemove(conversation);
                            inputRef.current?.focus();
                        }}
                    />
                </ul>
            ) : (
                recipients.length > 0 && (
                    <ul
                        ref={recipientsContainerRef}
                        className="flex max-h-[140px] flex-wrap gap-[4px] overflow-y-auto overscroll-contain"
                    >
                        {recipients.map((recipient) => (
                            <RecipientItem
                                key={`${recipient.__typename}-${recipient.ID}`}
                                avatar={
                                    <ParticipantAvatar
                                        className="size-[28px]"
                                        fallbackClassName="text-[13px]!"
                                        participant={recipient}
                                    />
                                }
                                name={
                                    recipient.__typename === 'Professional'
                                        ? recipient.Title
                                        : recipient.Name
                                }
                                isDisabled={isDisabled}
                                onRemove={() => {
                                    onRecipientsChange(
                                        recipients.filter(
                                            (r) =>
                                                r.__typename !==
                                                    recipient.__typename ||
                                                r.ID !== recipient.ID,
                                        ),
                                    );
                                    inputRef.current?.focus();
                                }}
                            />
                        ))}
                    </ul>
                )
            )}
            {!conversation && (
                <>
                    <Input
                        ref={(element) => {
                            inputRef.current = element ?? undefined;
                            onInputRef?.(element ?? undefined);
                        }}
                        value={search}
                        className="md:text-[14px]! rounded-[8px] bg-[#f2f2f2] text-[16px]"
                        color="contrast"
                        placeholder="Type a name to search profiles..."
                        autoComplete="off"
                        showClearButton
                        autoFocus
                        disabled={isDisabled}
                        onChange={(e) => onSearchChange(e.target.value)}
                    />
                    {recipients.length >= maxRecipients && !!search && (
                        <div className="px-[4px] py-[2px] text-[12px] text-red-600">
                            You can only add up to {maxRecipients} recipients
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function RecipientItem({
    avatar,
    name,
    isDisabled,
    onRemove,
}: {
    avatar: React.ReactNode;
    name: string;
    isDisabled: boolean;
    onRemove: () => void;
}) {
    return (
        <li className="flex items-center gap-[8px] overflow-hidden rounded-[8px] bg-gray-100 p-[4px]">
            {avatar}
            <div className="min-w-0 flex-1 truncate text-[12px] font-medium leading-tight">
                {name}
            </div>
            <button
                className="self-start p-[2px] disabled:pointer-events-none disabled:opacity-50"
                disabled={isDisabled}
                onClick={onRemove}
            >
                <XIcon
                    className="size-[12px] text-gray-600"
                    strokeWidth={2.5}
                />
            </button>
        </li>
    );
}
